import { useState, useEffect } from 'react';
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Drawer,
  DrawerCloseButton,
  DrawerOverlay,
  DrawerContent,
  DrawerFooter,
  DrawerBody,
  DrawerHeader,
  VStack,
  Center,
  HStack,
  Text,
  Button,
  Icon,
  Divider,
} from '@chakra-ui/react';

import { AiOutlineShop } from 'react-icons/ai';
import { IoCheckmarkOutline } from 'react-icons/io5';

import * as fp from 'lodash/fp';

import mediaQueryStateAtom from '~/recoil/atom/mediaQueryState';
import { useRecoilValue } from 'recoil';

import { AlgoliaStore } from '~/types/store/store';

type StoreFilterModalProps = {
  isOpen: boolean;
  stores: Array<AlgoliaStore>;
  selectedStoreIds: Array<string>;
  onClose: () => void;
  onConfirm: (stores: Array<string>) => void;
};

export default function StoreFilterModal({
  isOpen,
  stores,
  selectedStoreIds,
  onClose,
  onConfirm,
}: StoreFilterModalProps) {
  const [newSelectedStores, setNewSelectedStores] = useState<Array<string>>(selectedStoreIds);
  const { isLargerThan768 } = useRecoilValue(mediaQueryStateAtom);

  useEffect(() => {
    isOpen && setNewSelectedStores(selectedStoreIds);
  }, [isOpen, selectedStoreIds]);

  const handleOnClickStore = (storeId: string) => {
    const isIncluded = newSelectedStores.includes(storeId);

    setNewSelectedStores((prev) => (isIncluded ? fp.pull(storeId)(prev) : [...prev, storeId]));
  };

  if (isLargerThan768) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent h="95%">
          <ModalCloseButton />
          <ModalHeader borderBottom="1px solid" borderBottomColor="primary">
            <Center mt={4}>
              <HStack spacing={3}>
                <Icon as={AiOutlineShop} color="primary" boxSize="1.5rem" />
                <Text fontSize="xl" fontWeight="normal">
                  選擇餐廳
                </Text>
              </HStack>
            </Center>
          </ModalHeader>
          <ModalBody>
            <VStack
              justifyContent="flex-start"
              p={2}
              spacing={0}
              divider={<Divider color="gray.200" />}
            >
              <HStack
                justifyContent="space-between"
                alignSelf="stretch"
                onClick={() => setNewSelectedStores([])}
                cursor="pointer"
                py={2}
                key="all"
              >
                <Text
                  color={newSelectedStores.length === 0 ? 'primary' : 'fontColor'}
                  fontSize="md"
                  fontWeight="700"
                >
                  全部餐廳
                </Text>
                {newSelectedStores.length === 0 && (
                  <Icon as={IoCheckmarkOutline} color="primary" boxSize="1.5rem" />
                )}
              </HStack>
              {stores.map(({ objectID, name }) => (
                <HStack
                  justifyContent="space-between"
                  alignSelf="stretch"
                  onClick={() => handleOnClickStore(objectID)}
                  cursor="pointer"
                  py={2}
                  key={objectID}
                >
                  <Text
                    color={newSelectedStores.includes(objectID) ? 'primary' : 'fontColor'}
                    fontSize="md"
                    fontWeight="700"
                  >
                    {name}
                  </Text>
                  {newSelectedStores.includes(objectID) && (
                    <Icon as={IoCheckmarkOutline} color="primary" boxSize="1.5rem" />
                  )}
                </HStack>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter p={0}>
            <Button
              isFullWidth
              size="lg"
              borderRadius="none"
              onClick={() => onConfirm(newSelectedStores)}
              justifyContent="space-between"
            >
              <HStack
                w="36px"
                h="36px"
                borderRadius="5px"
                bgColor="#513F24"
                justifyContent="center"
              >
                <Text color="white" fontSize="md" fontWeight="700" textAlign="center">
                  {newSelectedStores.length}
                </Text>
              </HStack>
              <Text color="white" fontSize="md" fontWeight="700">
                篩選條件
              </Text>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }
  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="full">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottom="1px solid" borderBottomColor="primary">
          <Center mt={4}>
            <HStack spacing={3}>
              <Icon as={AiOutlineShop} color="primary" boxSize="1.5rem" />
              <Text fontSize="xl" fontWeight="normal">
                選擇餐廳
              </Text>
            </HStack>
          </Center>
        </DrawerHeader>
        <DrawerBody>
          <VStack
            justifyContent="flex-start"
            p={2}
            spacing={0}
            divider={<Divider color="gray.200" />}
          >
            <HStack
              justifyContent="space-between"
              alignSelf="stretch"
              onClick={() => setNewSelectedStores([])}
              cursor="pointer"
              py={2}
              key="all"
            >
              <Text
                color={newSelectedStores.length === 0 ? 'primary' : 'fontColor'}
                fontSize="md"
                fontWeight="700"
              >
                全部餐廳
              </Text>
              {newSelectedStores.length === 0 && (
                <Icon as={IoCheckmarkOutline} color="primary" boxSize="1.5rem" />
              )}
            </HStack>
            {stores.map(({ objectID, name }) => (
              <HStack
                justifyContent="space-between"
                alignSelf="stretch"
                onClick={() => handleOnClickStore(objectID)}
                cursor="pointer"
                py={2}
                key={objectID}
              >
                <Text
                  color={newSelectedStores.includes(objectID) ? 'primary' : 'fontColor'}
                  fontSize="md"
                  fontWeight="700"
                >
                  {name}
                </Text>
                {newSelectedStores.includes(objectID) && (
                  <Icon as={IoCheckmarkOutline} color="primary" boxSize="1.5rem" />
                )}
              </HStack>
            ))}
          </VStack>
        </DrawerBody>
        <DrawerFooter p={0} position="sticky" bottom={0}>
          <Button
            isFullWidth
            size="lg"
            borderRadius="none"
            onClick={() => onConfirm(newSelectedStores)}
            justifyContent="space-between"
          >
            <HStack w="36px" h="36px" borderRadius="5px" bgColor="#513F24" justifyContent="center">
              <Text color="white" fontSize="md" fontWeight="700" textAlign="center">
                {newSelectedStores.length}
              </Text>
            </HStack>
            <Text color="white" fontSize="md" fontWeight="700">
              篩選條件
            </Text>
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
